import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Container from '../components/Container';
import Button from '../components/Button';
import Footer from '../components/Footer';
import CookieLogo from '../assets/cookie.png';

// Import warmer screenshots
import activesessions from '../assets/screenshots/activesessions.jpg';
import profiles from '../assets/screenshots/profiles.jpg';
import scheduleview from '../assets/screenshots/scheduleview.jpg';
import history from '../assets/screenshots/history.jpg';
import guide from '../assets/screenshots/guide.jpg';
import support from '../assets/screenshots/support.jpg';
import logs from '../assets/screenshots/logs.jpg';
import schedulereation from '../assets/screenshots/schedulereation.jpg';
import dessiondetails from '../assets/screenshots/dessiondetails.jpg';
import update from '../assets/screenshots/update.jpg';

// Top Banner Component
const TopBanner = styled.div`
  width: 100%;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.9) 0%, rgba(110, 168, 255, 0.8) 100%);
  color: #fff;
  padding: 0.8rem 2rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(45, 108, 255, 0.3);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
  }
`;

const BannerLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #f0f7ff;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  animation: ${fadeIn} 0.8s ease-out;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 2rem 0 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem 0 0.5rem;
  }
`;

const ReferrerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
`;

const ReferrerLogo = styled.img`
  width: 180px;
  height: 180px;
  object-fit: contain;
  margin-bottom: 1rem;
  padding: 6px;
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: 0.5rem;
  }
`;

const ReferrerName = styled.h1`
  font-size: 2.1rem;
  color: #6ea8ff;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 12px #2d6cffcc;
`;

const ReferrerChannel = styled.div`
  font-size: 1.1rem;
  color: #b6d0ff;
  margin-bottom: 0.5rem;
`;

const ReferrerDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.1rem;
  max-width: 600px;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(44, 100, 255, 0.12);
  color:rgb(255, 45, 45);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 0 8px #2d6cff44;
`;

const ReferrerLogosRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 1.2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0 1rem;
  }
`;

const BigX = styled.div`
  font-size: 5rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 0 32px #fff, 0 0 16px #2d6cffcc, 0 0 8px #fff;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const ProductHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const ProductTitle = styled.h2`
  font-size: 3rem;
  color: #6ea8ff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 0 0 16px #2d6cffcc;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ProductSubtitle = styled.p`
  font-size: 1.3rem;
  color: #b6d0ff;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  max-width: 1800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled.div`
  background: rgba(20, 30, 60, 0.65);
  border-radius: 18px;
  padding: 2rem;
  border: 1.5px solid #2d6cff44;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #2d6cff;
    box-shadow: 0 12px 40px 0 rgba(45, 108, 255, 0.3);
    transform: translateY(-5px);
  }
`;

const FeatureTitle = styled.h3`
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`;

const FeatureDescription = styled.p`
  color: #b6d0ff;
  line-height: 1.6;
  font-size: 1rem;
`;

const OtherToolsLink = styled(Link)`
  display: inline-block;
  background: rgba(20, 30, 60, 0.65);
  color: #6ea8ff;
  text-decoration: none;
  padding: 0.75rem 2rem;
  border-radius: 12px;
  border: 1px solid #2d6cff44;
  font-weight: 600;
  transition: all 0.2s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: rgba(45, 108, 255, 0.12);
    border-color: #2d6cff;
    transform: translateY(-2px);
  }
`;

const ScreenshotsSection = styled.div`
  margin-bottom: 3rem;
`;

const CarouselContainer = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  background: transparent;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 800px;
  object-fit: contain;
  position: ${props => props.index === 0 ? 'relative' : 'absolute'};
  top: ${props => props.index === 0 ? 'auto' : '0'};
  left: ${props => props.index === 0 ? 'auto' : '0'};
  opacity: ${props => props.$active ? '1' : '0'};
  transition: opacity 0.5s ease-in-out;
  border-radius: 24px;
  
  @media (max-width: 768px) {
    height: 500px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#6ea8ff' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6ea8ff;
    transform: scale(1.2);
  }
`;

// New components for conversion optimization
const HookSection = styled.div`
  text-align: center;
  margin: 3rem 0;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.15) 0%, rgba(110, 168, 255, 0.1) 100%);
  border-radius: 24px;
  border: 2px solid #2d6cff44;
  backdrop-filter: blur(8px);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 2rem 1rem;
    border-radius: 16px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #6ea8ff, transparent);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const HookHeadline = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  color: #fff;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 20px #6ea8ff88;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
`;

const HookSubtext = styled.p`
  font-size: 1.4rem;
  color: #b6d0ff;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const UrgencyBanner = styled.div`
  background: linear-gradient(135deg, #ff4757 0%, #ff6b7a 100%);
  color: #fff;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  margin: 1.5rem auto;
  display: inline-block;
  box-shadow: 0 4px 20px rgba(255, 71, 87, 0.4);
  animation: pulse 2s infinite;
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
    margin: 1rem auto;
    border-radius: 25px;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
  color: #fff;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    margin: 1.5rem 0;
    padding: 0 0.5rem;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(45, 108, 255, 0.1);
  padding: 1rem;
  border-radius: 12px;
  color: #fff;
  border: 1px solid #2d6cff44;
  
  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
  
  &::before {
    content: '✅';
    margin-right: 0.75rem;
    font-size: 1.2rem;
  }
`;

const CTAButton = styled(Button)`
  background: linear-gradient(135deg, #2d6cff 0%, #6ea8ff 100%) !important;
  color: #fff !important;
  border: none !important;
  padding: 1.2rem 3rem !important;
  font-size: 1.3rem !important;
  font-weight: 700 !important;
  border-radius: 50px !important;
  box-shadow: 0 8px 25px rgba(45, 108, 255, 0.4) !important;
  transition: all 0.3s ease !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  
  @media (max-width: 768px) {
    padding: 1rem 2rem !important;
    font-size: 1rem !important;
    letter-spacing: 0.3px !important;
    border-radius: 30px !important;
  }
  
  &:hover {
    color: #fff !important;
    transform: translateY(-3px) !important;
    box-shadow: 0 12px 35px rgba(45, 108, 255, 0.6) !important;
  }
  
  &:active {
    transform: translateY(-1px) !important;
  }
`;

const TrustIndicators = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  margin: 2rem 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin: 1.5rem 0;
  }
`;

const TrustItem = styled.div`
  text-align: center;
  color: #b6d0ff;
`;

const TrustNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #6ea8ff;
  text-shadow: 0 0 10px #6ea8ff66;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TrustLabel = styled.div`
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const TestimonialsSection = styled.div`
  margin: 4rem 0;
  padding: 3rem 2rem;
  background: rgba(20, 30, 60, 0.65);
  border-radius: 24px;
  border: 1px solid #2d6cff44;
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 2rem 1rem;
    border-radius: 16px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 1.5rem;
  }
`;

const TestimonialCard = styled.div`
  background: rgba(45, 108, 255, 0.08);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid #2d6cff22;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const TestimonialText = styled.p`
  color: #fff;
  font-style: italic;
  margin-bottom: 1rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
  }
  
  &::before {
    content: '"';
    font-size: 2rem;
    color: #6ea8ff;
  }
  
  &::after {
    content: '"';
    font-size: 2rem;
    color: #6ea8ff;
  }
`;

const TestimonialAuthor = styled.div`
  color: #6ea8ff;
  font-weight: 600;
  
  &::before {
    content: '— ';
  }
`;

const PlatformIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const PlatformIcon = styled.div`
  background: rgba(45, 108, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border: 1px solid #2d6cff44;
  color: #b6d0ff;
  font-weight: 600;
  font-size: 0.9rem;
`;

// Warmer plans
const warmerPlans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 60,
    features: [
      '1 month access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Standard customer support',
      'Software updates included'
    ],
    featured: false
  },
  {
    id: 'quarterly',
    name: 'Quarterly',
    price: 140,
    features: [
      '3 months access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'Regular software updates and improvements',
      'Save $50 compared to monthly'
    ],
    featured: true
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: 300,
    features: [
      'Lifetime access to DeservedWarmer',
      'Full browser profile warming for Amazon, eBay, and Vinted',
      'Access to all automation features',
      'Natural browsing pattern simulation',
      'Cookie and session management',
      'Detailed activity logs',
      'AdsPower integration',
      'Priority customer support',
      'Advanced profile management tools',
      'All future updates and improvements',
      'One-time payment, no recurring fees'
    ],
    featured: true
  }
];

const cryptoOptions = [
  { id: 'ltc', name: 'Litecoin', icon: 'Ł' },
  { id: 'btc', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', name: 'Ethereum', icon: 'Ξ' },
  { id: 'usdttrc20', name: 'USDT (TRC-20)', icon: '₮', note: 'Low fees' },
  { id: 'usdterc20', name: 'USDT (ERC-20)', icon: '₮', note: 'Higher fees' }
];

// Warmer screenshots array
const warmerScreenshots = [
  { src: profiles, alt: "Profile Management Interface" },
  { src: activesessions, alt: "Active Sessions Dashboard" },
  { src: scheduleview, alt: "Schedule Management View" },
  { src: schedulereation, alt: "Schedule Creation Interface" },
  { src: history, alt: "Session History Tracking" },
  { src: dessiondetails, alt: "Detailed Session Information" },
  { src: logs, alt: "Activity Logs and Monitoring" },
  { src: guide, alt: "Built-in Guides and Documentation" },
  { src: support, alt: "Support and Help Interface" },
  { src: update, alt: "Update and Maintenance Features" }
];

function ReferralWarmerPage() {
  const { referrer } = useParams();
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const [selectedCrypto, setSelectedCrypto] = useState('ltc');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch referrer info
    fetch(`/api/referrer/${referrer}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setReferrerInfo(data.referrer);
        else setError('Referrer not found.');
      })
      .catch(() => setError('Error loading referrer info.'));
  }, [referrer]);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % warmerScreenshots.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const currentPlan = warmerPlans.find(plan => plan.id === selectedPlan);

  useEffect(() => {
    if (step === 2 && selectedCrypto && currentPlan) {
      // Get real exchange rate estimate from NOWPayments
      const getPaymentEstimate = async () => {
        try {
          const response = await fetch('https://api.nowpayments.io/v1/estimate', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: currentPlan.price,
              currency_from: 'usd',
              currency_to: selectedCrypto
            })
          });
          const data = await response.json();
          if (data.estimated_amount) {
            setPaymentEstimate({
              fiat: currentPlan.price,
              crypto: data.estimated_amount.toFixed(8),
              currency: selectedCrypto
            });
          }
        } catch (error) {
          console.error('Error getting estimate:', error);
          // Fallback to showing just USD if estimate fails
          setPaymentEstimate({
            fiat: currentPlan.price,
            crypto: '---',
            currency: selectedCrypto
          });
        }
      };
      getPaymentEstimate();
    }
  }, [step, selectedCrypto, selectedPlan, currentPlan]);

  const proceedToPayment = () => setStep(2);
  const handleCryptoSelect = (cryptoId) => setSelectedCrypto(cryptoId);

  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch('/api/refpayment/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlan,
          crypto_currency: selectedCrypto,
          referrer: referrer
        })
      });

      const data = await res.json();
      if (data.success) {
        const statusUrl = `/status/${data.payment.payment_id}`;
        navigate(statusUrl);
      } else {
        setError(data.message || 'Payment creation failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (error && !referrerInfo) {
    return (
      <PageWrapper>
        <TopBanner>
          Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
        </TopBanner>
        <Container>
          <ErrorMessage>{error}</ErrorMessage>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <TopBanner>
        Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
      </TopBanner>
      <ContentWrapper>
        <Container>
          {referrerInfo && (
            <ReferrerHeader>
              <ReferrerLogosRow>
                <ReferrerLogo src={CookieLogo} alt="Deserved Cookie Logo"/>
                <BigX>×</BigX>
                {referrerInfo.logo_url && <ReferrerLogo src={referrerInfo.logo_url} alt={referrerInfo.name} />}
              </ReferrerLogosRow>
              <ReferrerName>{referrerInfo.name}</ReferrerName>
              {referrerInfo.channel && <ReferrerChannel>{referrerInfo.channel}</ReferrerChannel>}
              {referrerInfo.description && <ReferrerDescription>{referrerInfo.description}</ReferrerDescription>}
            </ReferrerHeader>
          )}

          <HookSection>
            <UrgencyBanner>
              ⚡ Limited Time: Save $200 on Lifetime License
            </UrgencyBanner>
            <HookHeadline>
              💸 Stop Getting Banned – Warm Your Accounts Like a Pro
            </HookHeadline>
            <HookSubtext>
              Turn suspicious new accounts into trusted, high-value profiles. Our advanced automation creates human-like activity patterns that fool Amazon, eBay, and platform algorithms.
            </HookSubtext>
            
            <BenefitsList>
              <BenefitItem>Zero account suspensions</BenefitItem>
              <BenefitItem>Human-like browsing patterns</BenefitItem>
              <BenefitItem>Works 24/7 automatically</BenefitItem>
              <BenefitItem>Amazon, eBay, Vinted support</BenefitItem>
            </BenefitsList>

            <CTAButton 
              onClick={() => {
                setSelectedPlan('lifetime');
                // Smooth scroll to pricing section
                setTimeout(() => {
                  const pricingSection = document.querySelector('[data-pricing-section]');
                  if (pricingSection) {
                    pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
            >
              🚀 Get Lifetime Access - Save $200
            </CTAButton>

            <TrustIndicators>
              <TrustItem>
                <TrustNumber>150+</TrustNumber>
                <TrustLabel>Happy Customers</TrustLabel>
              </TrustItem>
              <TrustItem>
                <TrustNumber>98.2%</TrustNumber>
                <TrustLabel>Ban Prevention Rate</TrustLabel>
              </TrustItem>
              <TrustItem>
                <TrustNumber>6+ Months</TrustNumber>
                <TrustLabel>Proven Track Record</TrustLabel>
              </TrustItem>
            </TrustIndicators>
          </HookSection>

          <ProductHeader>
            <OtherToolsLink to={`/ref/${referrer}`}>
              ← View All Tools
            </OtherToolsLink>
            <ProductTitle>The Problem: New Accounts Get Flagged Instantly</ProductTitle>
            <ProductSubtitle>
              Platforms like Amazon and eBay use sophisticated algorithms to detect suspicious account behavior. 
              One wrong move and your account is suspended. DeservedWarmer solves this with realistic automation that builds account trust over time.
            </ProductSubtitle>
          </ProductHeader>

          <ScreenshotsSection>
            <h3 style={{ color: '#fff', marginBottom: '2rem', fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.8rem', textAlign: 'center' }}>
              Application Screenshots
            </h3>
            <CarouselContainer>
              {warmerScreenshots.map((screenshot, index) => (
                <CarouselImage
                  key={index}
                  index={index}
                  src={screenshot.src}
                  alt={screenshot.alt}
                  $active={index === currentImageIndex}
                />
              ))}
              <CarouselControls>
                {warmerScreenshots.map((_, index) => (
                  <CarouselDot
                    key={index}
                    $active={index === currentImageIndex}
                    onClick={() => goToImage(index)}
                  />
                ))}
              </CarouselControls>
            </CarouselContainer>
          </ScreenshotsSection>

          <FeaturesGrid>
            <FeatureCard>
              <FeatureTitle>Automated Account Warming</FeatureTitle>
              <FeatureDescription>
                Automatically warm up your accounts with realistic browsing patterns, searches, 
                and interactions across Amazon, eBay, and Vinted platforms.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Human-Like Behavior</FeatureTitle>
              <FeatureDescription>
                Advanced algorithms simulate genuine user behavior including natural mouse movements, 
                realistic reading times, and authentic browsing patterns.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Privacy & Security</FeatureTitle>
              <FeatureDescription>
                Runs completely locally on your machine. No data sent to external servers. 
                Full proxy support and browser fingerprint management.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Detailed Analytics</FeatureTitle>
              <FeatureDescription>
                Comprehensive activity logs, session tracking, and performance analytics 
                to monitor your account warming progress.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>AdsPower Integration</FeatureTitle>
              <FeatureDescription>
                Seamless integration with AdsPower browser profiles for advanced 
                multi-account management and fingerprint isolation.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Premium Support</FeatureTitle>
              <FeatureDescription>
                Direct access to our support team via Telegram, regular updates, 
                and community of successful account warming experts.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>

          <div style={{ textAlign: 'center', margin: '3rem 0' }}>
            <h3 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.8rem', marginBottom: '2rem' }}>
              Supported Platforms & Integrations
            </h3>
            <PlatformIcons>
              <PlatformIcon>🛒 Amazon Marketplace</PlatformIcon>
              <PlatformIcon>🛍️ eBay Selling</PlatformIcon>
              <PlatformIcon>👗 Vinted Fashion</PlatformIcon>
              <PlatformIcon>🌐 AdsPower Profiles</PlatformIcon>
              <PlatformIcon>🔧 Proxy Support</PlatformIcon>
              <PlatformIcon>📊 Advanced Analytics</PlatformIcon>
            </PlatformIcons>
          </div>

          <TestimonialsSection>
            <h3 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem', textAlign: 'center', marginBottom: '1rem' }}>
              Success Stories From Our Community
            </h3>
            <p style={{ color: '#b6d0ff', textAlign: 'center', marginBottom: '2rem' }}>
              See how account warming transformed their businesses
            </p>
            
            <TestimonialGrid>
              <TestimonialCard>
                <TestimonialText>
                  Went from getting banned every week to running 15 stable Amazon accounts. This tool literally saved my business and made me 6 figures.
                </TestimonialText>
                <TestimonialAuthor>@amazon_king_2024</TestimonialAuthor>
              </TestimonialCard>

              <TestimonialCard>
                <TestimonialText>
                  The automation is incredible. Set it up once and my accounts look completely natural. eBay even gave me Top Rated Seller status.
                </TestimonialText>
                <TestimonialAuthor>@ecom_master_pro</TestimonialAuthor>
              </TestimonialCard>

              <TestimonialCard>
                <TestimonialText>
                  Support team helped me scale to 50+ accounts across multiple platforms. ROI paid for itself in the first month.
                </TestimonialText>
                <TestimonialAuthor>@dropship_empire</TestimonialAuthor>
              </TestimonialCard>
            </TestimonialGrid>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <CTAButton 
                onClick={() => {
                  setSelectedPlan('lifetime');
                  // Smooth scroll to pricing section
                  setTimeout(() => {
                    const pricingSection = document.querySelector('[data-pricing-section]');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 100);
                }}
              >
                Start Building Trusted Accounts Now
              </CTAButton>
            </div>
          </TestimonialsSection>
          
          {step === 1 ? (
            <div data-pricing-section>
              <div style={{ textAlign: 'center', margin: '4rem 0 3rem' }}>
                <h2 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.5rem', fontWeight: '900', marginBottom: '1rem' }}>
                  💰 Choose Your Account Warming Plan
                </h2>
                <p style={{ color: '#ff4757', fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem', fontWeight: '600', marginBottom: '2rem' }}>
                  🔥 Limited time: $200 off lifetime license
                </p>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: window.innerWidth <= 768 ? '1rem' : '1.5rem',
                  marginBottom: '1rem',
                  maxWidth: 1200,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%',
                  overflowX: 'visible',
                  flexWrap: 'wrap',
                  alignItems: 'stretch',
                  padding: window.innerWidth <= 768 ? '0 0.5rem' : '0'
                }}
              >
                {warmerPlans.map(plan => (
                  <div
                    key={plan.id}
                    style={{
                      background: selectedPlan === plan.id 
                        ? 'linear-gradient(135deg, rgba(45, 108, 255, 0.15) 0%, rgba(110, 168, 255, 0.1) 100%)'
                        : 'rgba(20, 30, 60, 0.65)',
                      borderRadius: window.innerWidth <= 768 ? '16px' : '24px',
                      padding: window.innerWidth <= 768 ? '1.5rem 1rem' : '2rem 1.5rem',
                      boxShadow: selectedPlan === plan.id 
                        ? '0 20px 60px 0 rgba(45, 108, 255, 0.4)' 
                        : '0 8px 32px 0 rgba(34, 60, 120, 0.18)',
                      border: selectedPlan === plan.id 
                        ? '2px solid #6ea8ff' 
                        : plan.featured 
                          ? '2px solid #2d6cff44' 
                          : '1.5px solid #2d6cff44',
                      backdropFilter: 'blur(12px)',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      minWidth: window.innerWidth <= 768 ? '280px' : '300px',
                      maxWidth: window.innerWidth <= 768 ? '100%' : '350px',
                      flex: window.innerWidth <= 768 ? '1' : '1 1 300px',
                      height: window.innerWidth <= 768 ? 'auto' : '700px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                                         {plan.featured && (
                       <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #ff4757 0%, #ff6b7a 100%)', color: '#fff', padding: window.innerWidth <= 768 ? '0.5rem 1rem' : '0.6rem 1.5rem', borderRadius: window.innerWidth <= 768 ? '15px' : '20px', fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.85rem', fontWeight: 'bold', boxShadow: '0 6px 20px rgba(255, 71, 87, 0.4)', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                         {plan.name === 'Lifetime' ? '🚀 BEST VALUE' : '💎 POPULAR'}
                       </div>
                     )}
                    
                                         <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', paddingTop: plan.featured ? '1rem' : '0.5rem' }}>
                       <h3 style={{ fontSize: window.innerWidth <= 768 ? '1.3rem' : '1.6rem', fontWeight: '900', marginBottom: '1rem', color: '#fff', textShadow: selectedPlan === plan.id ? '0 0 12px #6ea8ff88' : 'none' }}>
                         {plan.name}
                       </h3>
                       
                       <div style={{ marginBottom: '1.5rem' }}>
                         {plan.name === 'Lifetime' && (
                           <div style={{ fontSize: '0.95rem', color: '#ff4757', textDecoration: 'line-through', marginBottom: '0.5rem' }}>
                             $500 USD
                           </div>
                         )}
                         <div style={{ fontSize: window.innerWidth <= 768 ? '2.2rem' : '2.8rem', fontWeight: '900', color: '#6ea8ff', textShadow: '0 0 16px #6ea8ff88', lineHeight: '1' }}>
                           ${plan.price}
                         </div>
                         <div style={{ fontSize: '0.85rem', color: '#b6d0ff', marginTop: '0.5rem' }}>
                           {plan.name === 'Lifetime' ? 'One-time payment' : plan.name === 'Quarterly' ? 'Every 3 months' : 'Monthly billing'}
                         </div>
                         {plan.name === 'Lifetime' && (
                           <div style={{ fontSize: '0.75rem', color: '#2d6cff', marginTop: '0.5rem', fontWeight: '600' }}>
                             💰 Save $200 (40% off)
                           </div>
                         )}
                       </div>
                      
                                             <div style={{ background: 'rgba(45, 108, 255, 0.1)', borderRadius: window.innerWidth <= 768 ? '12px' : '16px', padding: window.innerWidth <= 768 ? '1rem' : '1.2rem', marginBottom: '1.5rem', textAlign: 'left', flex: 1 }}>
                         <ul style={{ listStyle: 'none', padding: 0, color: '#b6d0ff', margin: 0 }}>
                           {plan.features.slice(0, 6).map((f, i) => (
                             <li key={i} style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem', position: 'relative', fontSize: '0.85rem', lineHeight: '1.3' }}>
                               <span style={{ position: 'absolute', left: 0, color: '#2d6cff', fontWeight: 'bold', fontSize: '1rem' }}>✓</span> {f}
                             </li>
                           ))}
                           {plan.features.length > 6 && (
                             <li style={{ color: '#6ea8ff', fontWeight: 600, fontSize: '0.8rem', textAlign: 'center', marginTop: '0.8rem' }}>
                               + {plan.features.length - 6} more features
                             </li>
                           )}
                         </ul>
                       </div>

                                             <div style={{ marginTop: 'auto' }}>
                         <CTAButton
                           onClick={() => { setSelectedPlan(plan.id); proceedToPayment(); }}
                           style={{ 
                             width: '100%', 
                             fontSize: window.innerWidth <= 768 ? '0.95rem' : '1.1rem', 
                             padding: window.innerWidth <= 768 ? '1rem 1rem' : '1.1rem 1.5rem',
                             opacity: selectedPlan === plan.id ? 1 : 0.8,
                             background: selectedPlan === plan.id 
                               ? 'linear-gradient(135deg, #2d6cff 0%, #6ea8ff 100%) !important'
                               : plan.featured 
                                 ? 'linear-gradient(135deg, #2d6cff 0%, #6ea8ff 100%) !important'
                                 : 'linear-gradient(135deg, rgba(45, 108, 255, 0.7) 0%, rgba(110, 168, 255, 0.7) 100%) !important'
                           }}
                         >
                           {selectedPlan === plan.id ? '🚀 Continue to Payment' : plan.featured ? '💎 Get Best Value' : 'Select Plan'}
                         </CTAButton>
                         
                         {plan.featured && (
                           <div style={{ marginTop: '0.8rem', fontSize: '0.75rem', color: '#b6d0ff', lineHeight: '1.3' }}>
                             ⚡ Most popular choice<br/>
                             🛡️ Best value for money
                           </div>
                         )}
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div style={{marginTop:'2rem',marginBottom:'2rem',background:'rgba(20,30,60,0.65)',borderRadius:'18px',padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',boxShadow:'0 8px 32px 0 rgba(34, 60, 120, 0.18)',border:'1.5px solid #2d6cff44',backdropFilter:'blur(8px)'}}>
                <h3 style={{fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem',marginBottom:'1rem',textAlign:'center',color:'#fff',textShadow:'0 0 8px #2d6cffcc'}}>Select Payment Method</h3>
                
                {/* Main Cryptocurrencies Row */}
                <div style={{display:'grid',gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',marginTop:'1rem',justifyContent:'center'}}>
                  {cryptoOptions.filter(crypto => ['ltc', 'btc', 'eth'].includes(crypto.id)).map(crypto => (
                    <div key={crypto.id} style={{padding: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',border:`2px solid ${selectedCrypto === crypto.id ? '#2d6cff' : '#2d6cff44'}`,borderRadius:'8px',textAlign:'center',cursor:'pointer',transition:'all 0.2s ease',background:selectedCrypto === crypto.id ? 'rgba(44, 108, 255, 0.12)' : 'rgba(20, 30, 60, 0.65)',color:'#b6d0ff',boxShadow:selectedCrypto === crypto.id ? '0 0 12px #2d6cff88' : 'none'}} onClick={() => handleCryptoSelect(crypto.id)}>
                      <div style={{fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',marginBottom:'0.5rem',color:'#2d6cff',textShadow:'0 0 8px #2d6cffcc'}}>{crypto.icon}</div>
                      <div style={{fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.8rem',fontWeight:selectedCrypto === crypto.id ? 'bold' : 'normal',color:'#b6d0ff'}}>{crypto.name}</div>
                      {crypto.note && (
                        <div style={{fontSize: window.innerWidth <= 768 ? '0.6rem' : '0.7rem',marginTop:'0.3rem',color:'#b6d0ff'}}>{crypto.note}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* USDT Options Row - Centered */}
                <div style={{display:'flex',justifyContent:'center',gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',marginTop:'1rem',flexWrap:'wrap'}}>
                  {cryptoOptions.filter(crypto => crypto.id.includes('usdt')).map(crypto => (
                    <div key={crypto.id} style={{padding: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',border:`2px solid ${selectedCrypto === crypto.id ? '#2d6cff' : '#2d6cff44'}`,borderRadius:'8px',textAlign:'center',cursor:'pointer',transition:'all 0.2s ease',background:selectedCrypto === crypto.id ? 'rgba(44, 108, 255, 0.12)' : 'rgba(20, 30, 60, 0.65)',color:'#b6d0ff',boxShadow:selectedCrypto === crypto.id ? '0 0 12px #2d6cff88' : 'none',minWidth: window.innerWidth <= 768 ? '100px' : '120px'}} onClick={() => handleCryptoSelect(crypto.id)}>
                      <div style={{fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',marginBottom:'0.5rem',color:'#2d6cff',textShadow:'0 0 8px #2d6cffcc'}}>{crypto.icon}</div>
                      <div style={{fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.8rem',fontWeight:selectedCrypto === crypto.id ? 'bold' : 'normal',color:'#b6d0ff'}}>{crypto.name}</div>
                      {crypto.note && (
                        <div style={{fontSize: window.innerWidth <= 768 ? '0.6rem' : '0.7rem',marginTop:'0.3rem',color:'#b6d0ff'}}>{crypto.note}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {paymentEstimate && (
                <div style={{textAlign:'center',marginBottom:'2rem',background:'rgba(20,30,60,0.65)',borderRadius:'18px',padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',boxShadow:'0 8px 32px 0 rgba(34, 60, 120, 0.18)',border:'1.5px solid #2d6cff44',backdropFilter:'blur(8px)'}}>
                  <h4 style={{color:'#fff',marginBottom:'1rem',textShadow:'0 0 8px #2d6cffcc',fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem'}}>Payment Summary</h4>
                  <p style={{color:'#b6d0ff',marginBottom:'0.5rem',fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>Plan: <strong style={{color:'#6ea8ff'}}>{currentPlan?.name}</strong></p>
                  <p style={{color:'#b6d0ff',marginBottom:'0.5rem',fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>Amount: <strong style={{color:'#6ea8ff'}}>{paymentEstimate.crypto} {paymentEstimate.currency.toUpperCase()}</strong></p>
                  <p style={{color:'#b6d0ff',fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem'}}>≈ ${paymentEstimate.fiat} USD</p>
                </div>
              )}
              <div style={{display:'flex',justifyContent:'center',gap:'1rem',flexWrap:'wrap'}}>
                <Button onClick={() => setStep(1)} style={{minWidth:'120px'}}>Back</Button>
                <Button primary onClick={handlePurchase} disabled={loading} style={{minWidth:'120px'}}>
                  {loading ? 'Processing...' : 'Purchase Now'}
                </Button>
              </div>
            </>
          )}
        </Container>
      </ContentWrapper>
      <Footer />
    </PageWrapper>
  );
}

export default ReferralWarmerPage; 